import React, { useRef, useEffect, useState } from "react";
import { saveInspectionChecklistScope } from "../../helpers/backend_helper";

const sectionName = (q) => q.checklistArea?.name || q.areaName || "Unassigned";
export default function ChecklistQuestionScope({
  reviewId,
  checklist,
  onSaved,
  lockedQuestionIds = [],
}) {
  const questions = checklist.questions;
  const [selected, setSelected] = useState(() =>
    questions.filter((q) => q.isInScope !== false).map((q) => q.id)
  );
  const [savedSelection, setSavedSelection] = useState(() =>
    questions.filter((q) => q.isInScope !== false).map((q) => q.id)
  );
  const changed = (id) => selected.includes(id) !== savedSelection.includes(id);
  const addedCount = selected.filter(
    (id) => !savedSelection.includes(id)
  ).length;
  const removedCount = savedSelection.filter(
    (id) => !selected.includes(id)
  ).length;
  const [search, setSearch] = useState("");
  const [section, setSection] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ascending, setAscending] = useState(true);
  const allCheckbox = useRef(null);
  const locked = (q) =>
    q.isInScope !== false &&
    (lockedQuestionIds.includes(q.id) ||
      Number(q.inspectionStatusId) === 3 ||
      Boolean(q.inspectionResponse?.trim()));
  const visible = questions
    .filter((q) => {
      const term = search.trim().toLowerCase();
      return (
        (!term ||
          `${q.referenceNo || ""} ${q.question || ""} ${sectionName(q)}`
            .toLowerCase()
            .includes(term)) &&
        (!section || sectionName(q) === section) &&
        (!status ||
          changed(q.id) ||
          (status === "selected"
            ? selected.includes(q.id)
            : !selected.includes(q.id)))
      );
    })
    .sort(
      (a, b) =>
        (ascending ? 1 : -1) *
        (a.referenceNo || "").localeCompare(b.referenceNo || "", undefined, {
          numeric: true,
        })
    );
  const allSelected =
    visible.length > 0 && visible.every((q) => selected.includes(q.id));
  const someSelected = visible.some((q) => selected.includes(q.id));
  useEffect(() => {
    if (allCheckbox.current)
      allCheckbox.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);
  const changeSelection = (ids) => {
    setSelected(ids);
    setError("");
  };
  const selectVisible = () => {
    const ids = new Set(visible.filter((q) => !locked(q)).map((q) => q.id));
    changeSelection(
      allSelected
        ? selected.filter((id) => !ids.has(id))
        : [...new Set([...selected, ...visible.map((q) => q.id)])]
    );
  };
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await saveInspectionChecklistScope(
        reviewId,
        checklist.id,
        selected
      );
      if (!response?.status)
        throw new Error(response?.message || "Unable to save question scope.");
      setSavedSelection([...selected]);
      onSaved();
    } catch (reason) {
      setError(reason.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <section
      className="checklist-question-scope"
      aria-label="Define question scope"
    >
      <header className="checklist-question-scope__header">
        <span className="checklist-question-scope__step" aria-hidden="true">
          1
        </span>
        <span>
          <strong>
            Define question scope{" "}
            <span>
              ({selected.length} of {questions.length})
            </span>
          </strong>
          <span className="checklist-question-scope__hint">
            Select the questions to include in this review. Questions with
            existing review work cannot be excluded.
          </span>
        </span>
        <button
          type="button"
          disabled={saving || !selected.length}
          onClick={save}
        >
          {saving ? "Saving…" : "Save question scope"}
        </button>
      </header>
      {(addedCount > 0 || removedCount > 0) && (
        <p className="checklist-question-scope__changes" role="status">
          Unsaved changes: {addedCount} added · {removedCount} removed. Changed
          questions stay visible until saved.
        </p>
      )}
      <div className="checklist-question-scope__toolbar">
        <div className="checklist-question-scope__search">
          <i className="bx bx-search" aria-hidden="true" />
          <input
            aria-label="Search scope questions"
            placeholder="Search questions by number, keyword or text…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <span className="checklist-question-scope__filter-label">
          <i className="bx bx-filter-alt" aria-hidden="true" /> Filters
        </span>
        <select
          aria-label="Filter scope by section"
          value={section}
          onChange={(event) => setSection(event.target.value)}
        >
          <option value="">All sections</option>
          {[...new Set(questions.map(sectionName))].sort().map((name) => (
            <option key={name}>{name}</option>
          ))}
        </select>
        <select
          aria-label="Filter scope by status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="">All status</option>
          <option value="selected">Selected</option>
          <option value="unselected">Not selected</option>
        </select>
        <button
          className="checklist-question-scope__select"
          type="button"
          disabled={saving || !visible.length}
          onClick={selectVisible}
        >
          <i
            className={`bx ${
              allSelected ? "bx-checkbox-checked" : "bx-checkbox"
            }`}
            aria-hidden="true"
          />
          {allSelected ? "Deselect all" : "Select all"}
        </button>
      </div>
      <div className="checklist-question-scope__table-wrap">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  ref={allCheckbox}
                  type="checkbox"
                  aria-label="Select all visible questions"
                  checked={allSelected}
                  disabled={saving || !visible.length}
                  onChange={selectVisible}
                />
              </th>
              <th>
                <button
                  type="button"
                  aria-label="Sort questions by number"
                  onClick={() => setAscending(!ascending)}
                >
                  #{" "}
                  <i
                    className={`bx bx-sort-${ascending ? "down" : "up"}`}
                    aria-hidden="true"
                  />
                </button>
              </th>
              <th>Question</th>
              <th>Section</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((q) => (
              <tr
                key={q.id}
                className={
                  changed(q.id)
                    ? "checklist-question-scope__changed-row"
                    : undefined
                }
              >
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Include ${q.referenceNo || q.question}`}
                    title={
                      locked(q)
                        ? "Questions with existing review work cannot be excluded."
                        : undefined
                    }
                    checked={selected.includes(q.id)}
                    disabled={saving || locked(q)}
                    onChange={(event) =>
                      changeSelection(
                        event.target.checked
                          ? [...selected, q.id]
                          : selected.filter((id) => id !== q.id)
                      )
                    }
                  />
                </td>
                <td className="checklist-question-scope__number">
                  {q.referenceNo || "—"}
                </td>
                <td>
                  <span
                    className="checklist-question-scope__question"
                    title={q.question}
                  >
                    {q.question}
                  </span>
                </td>
                <td>
                  <span className="checklist-question-scope__section">
                    {sectionName(q)}
                  </span>
                </td>
                <td>
                  <span
                    className={`checklist-question-scope__status${
                      selected.includes(q.id) ? " is-selected" : ""
                    }`}
                  >
                    {selected.includes(q.id) ? "Selected" : "Not selected"}
                  </span>
                  {changed(q.id) && (
                    <span className="checklist-question-scope__pending">
                      {selected.includes(q.id) ? "Newly selected · unsaved" : "To be removed · unsaved"}
                    </span>
                  )}
                  {locked(q) && (
                    <span className="checklist-question-scope__locked">
                      <i className="bx bx-lock-alt" aria-hidden="true" /> Review
                      work
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!visible.length && (
          <p className="checklist-question-scope__empty">
            No questions match these filters.
          </p>
        )}
      </div>
      <footer>
        <span>
          {selected.length} of {questions.length} questions selected
          {(search || section || status) && (
            <small>
              {" "}
              · Showing {visible.length} of {questions.length}
            </small>
          )}
        </span>
      </footer>
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
